import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { SkeletonCard } from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import { PlacementHub as AnnouncementFeed } from '../components/player/AnnouncementCard';
import UpcomingAssessmentCard from '../components/player/UpcomingAssessmentCard';
import BatchCard from '../components/player/BatchCard';
import Leaderboard from '../components/player/Leaderboard';
import MotivationMoment from '../components/player/MotivationMoment';
import LevelBadge from '../components/player/LevelBadge';
import XPBar from '../components/player/XPBar';
import Streak from '../components/player/Streak';
import { useStudent } from '../components/student/StudentContext';
import './PlacementHub.css';

// The student's placement companion — upcoming assessments, placement updates (real announcements from the college's
// placement cell), personal progress, batch and leaderboard. Personal progression outranks competition: "Your progress"
// comes before rank. Only CURRENT level/XP/streak is shown; no historical level snapshot exists in the schema, so a
// "+2 levels this month" number would be a guess and is not shown.
const PlacementHubPage = () => {
  const navigate = useNavigate();
  const { player } = useStudent();
  const [tests, setTests] = useState(null);
  const [batch, setBatch] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(null);

  const load = () => {
    setLoading(true); setFailed(null);
    Promise.allSettled([
      API.get('/portal/tests'),
      API.get('/portal/batch'),
      API.get('/portal/leaderboard'),
      API.get('/portal/announcements'),
    ]).then(([testsRes, batchRes, lbRes, annRes]) => {
      if (testsRes.status === 'fulfilled') setTests(testsRes.value.data);
      if (batchRes.status === 'fulfilled') setBatch(batchRes.value.data);
      if (lbRes.status === 'fulfilled') setLeaderboard(lbRes.value.data);
      if (annRes.status === 'fulfilled') setAnnouncements(annRes.value.data);
      if ([testsRes, batchRes, lbRes, annRes].every(r => r.status === 'rejected')) setFailed(testsRes.reason);
    }).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  if (loading) {
    return <div className="z-page" role="status" aria-label="Loading placement hub"><SkeletonCard lines={2} /><SkeletonCard lines={3} /></div>;
  }
  if (failed) return <div className="z-page"><ErrorState error={failed} onRetry={load} /></div>;

  const upcoming = (tests || []).filter(t => t.attempt_status !== 'submitted');

  return (
    <div className="z-page ph-body">
      <section className="z-section" aria-labelledby="ph-upcoming">
        <h2 className="z-section-title" id="ph-upcoming">Upcoming</h2>
        {upcoming.length > 0 ? (
          <>
            <div className="ph-list">
              {upcoming.slice(0, 3).map(t => (
                <UpcomingAssessmentCard key={t.id} test={t} onView={() => navigate('/student/tests')} />
              ))}
            </div>
            <MotivationMoment context="before_assessment" seed={upcoming[0]?.id} />
          </>
        ) : (
          <EmptyState icon="calendar" art={false} compact tone="neutral" title="You're all clear for now" body="Assessments your college schedules will appear here." />
        )}
      </section>

      <section className="z-section" aria-labelledby="ph-updates">
        <div className="z-section-head">
          <h2 className="z-section-title" id="ph-updates">Placement updates</h2>
          <button type="button" className="z-section-link" onClick={() => navigate('/student/notifications')}>Notifications</button>
        </div>
        <AnnouncementFeed announcements={announcements} title={null} />
      </section>

      {player && (
        <section className="z-section" aria-labelledby="ph-progress">
          <h2 className="z-section-title" id="ph-progress">Your progress</h2>
          <div className="z-surface ph-progress-card">
            <div className="ph-progress-top">
              <LevelBadge level={player.level} size="md" />
              <Streak streak={player.streak} compact />
            </div>
            <XPBar levelProgress={player.levelProgress} compact />
          </div>
        </section>
      )}

      {batch && (
        <section className="z-section" aria-labelledby="ph-batch">
          <h2 className="z-section-title" id="ph-batch">Your batch</h2>
          <BatchCard batch={batch} />
        </section>
      )}

      <section className="z-section" aria-labelledby="ph-lb">
        <h2 className="z-section-title" id="ph-lb">Batch leaderboard</h2>
        <Leaderboard leaderboard={leaderboard} />
      </section>
    </div>
  );
};

export default PlacementHubPage;
